/**
 * Device aggregate.
 *
 * IMEI / serial are sourced from the physical device; `value` is the
 * retail-equivalent price in the country's currency (used for risk and
 * recovery calculations).
 *
 * `controlProvider` is a logical name ("knox", "android-do", "ios-mdm")
 * resolved at runtime by @dfp/provider-contract to the actual adapter.
 * `capabilities` is a frozen snapshot of what THIS device can do under THIS
 * provider — captured at enrollment so policy decisions don't ask the
 * provider over the network on every evaluation.
 */
import { z } from "zod";
import { TERMINAL_DEVICE_STATES, } from "./states.js";
const lifecycleStateSchema = z.enum([
    "PROCURED",
    "WAREHOUSE_RECEIVED",
    "DEVICE_REGISTERED",
    "CONTROL_ENROLLED",
    "ASSIGNED_TO_DEALER",
    "RESERVED_FOR_CUSTOMER",
    "HANDED_OVER",
    "ACTIVE_FINANCED",
    "PENDING_RELEASE",
    "PAID_OFF",
    "RELEASED",
    "RETURNED",
    "REPOSSESSING",
    "REPOSSESSED",
    "REFURBISHING",
    "READY_FOR_RESALE",
    "DAMAGED",
    "LOST",
    "STOLEN",
    "WRITTEN_OFF",
    "RESOLD",
]);
// IMEI is 15 digits; Luhn-mod-10 is checked at the validator port, not here.
const imeiRegex = /^\d{15}$/;
export const deviceSchema = z.object({
    id: z.string().uuid(),
    countryId: z.string().uuid(),
    imei: z.string().regex(imeiRegex, "IMEI must be 15 digits"),
    serial: z.string().min(1),
    brand: z.string().min(1),
    model: z.string().min(1),
    source: z.enum(["warehouse", "dealer_provisioned"]).default("warehouse"),
    value: z.object({
        amountMinor: z.number().int(),
        currency: z.string().length(3),
    }),
    downPaymentMinor: z.number().int().nonnegative().optional(),
    lendingPrice: z
        .discriminatedUnion("mode", [
        z.object({
            mode: z.literal("fixed"),
            totalMinor: z.number().int().nonnegative(),
        }),
        z.object({
            mode: z.literal("per_period"),
            perPeriod: z.discriminatedUnion("basis", [
                z.object({ basis: z.literal("amount"), perPeriodMinor: z.number().int().nonnegative() }),
                z.object({ basis: z.literal("percent"), rateBps: z.number().int().nonnegative() }),
            ]),
        }),
        z.object({
            mode: z.literal("per_tenor"),
            perTenor: z
                .array(z.object({
                tenorMonths: z.number().int().positive(),
                totalMinor: z.number().int().nonnegative(),
            }))
                .min(1),
        }),
    ])
        .optional(),
    modelRiskClass: z.enum(["A", "B", "C"]),
    controlProvider: z.string().min(1),
    capabilities: z.array(z.string()).default([]),
    lifecycleState: lifecycleStateSchema,
    assignedDealerId: z.string().uuid().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export function makeDevice(raw) {
    const parsed = deviceSchema.parse(raw);
    return {
        id: parsed.id,
        countryId: parsed.countryId,
        imei: parsed.imei,
        serial: parsed.serial,
        brand: parsed.brand,
        model: parsed.model,
        source: parsed.source,
        value: {
            amountMinor: parsed.value.amountMinor,
            currency: parsed.value.currency,
        },
        ...(parsed.downPaymentMinor !== undefined ? { downPaymentMinor: parsed.downPaymentMinor } : {}),
        ...(parsed.lendingPrice !== undefined ? { lendingPrice: parsed.lendingPrice } : {}),
        modelRiskClass: parsed.modelRiskClass,
        controlProvider: parsed.controlProvider,
        capabilities: new Set(parsed.capabilities.map((c) => c)),
        lifecycleState: parsed.lifecycleState,
        ...(parsed.assignedDealerId !== undefined
            ? { assignedDealerId: parsed.assignedDealerId }
            : {}),
        createdAt: parsed.createdAt,
        updatedAt: parsed.updatedAt,
    };
}
export function isTerminal(device) {
    return TERMINAL_DEVICE_STATES.has(device.lifecycleState);
}
//# sourceMappingURL=device.js.map